/**
 * Read a video file's duration in the browser, before it is uploaded.
 *
 * Duration has been hand-typed in raw seconds until now, with a silent NaN drop
 * on anything else — so most lessons carry no duration at all and every "X
 * hours of content" figure on a sales page understates the course. The file is
 * already on this machine; the browser can just tell us.
 *
 * Best-effort by design: a codec the browser cannot parse resolves to null and
 * the author's own value (or none) stands.
 */
export function probeVideoDuration(file: File, timeoutMs = 10_000): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.addEventListener('loadedmetadata', () => {
      const seconds = video.duration;
      finish(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null);
    });
    video.addEventListener('error', () => finish(null));
    video.src = url;
  });
}
